import React, { useMemo, useState } from "react";
import { foldUnchanged, objectDiffToSegments } from "../../Helper/yamlDiff";
import { diffObjects, type ObjectChange } from "./objectChanges";
import { MediaValue, detectMedia } from "./MediaValue";

/**
 * LE widget de comparaison avant/après du CMS : deux documents JSON, deux rendus commutables.
 * « Par blocs » : une carte par champ/composant touché (ajouté vert, retiré rouge, modifié
 * ambre, déplacé bleu), lisible par un rédacteur. « Texte unifié » : le diff ligne à ligne
 * façon GitHub, exhaustif, pour l'œil technique. « Fichier source » / « Fichier servi » :
 * chaque document COMPLET (JSON indenté) avec le bandeau des poids (source, servi, écart),
 * pour juger la taille réelle de l'artefact. Consommé par la page Publication (brouillon
 * contre déployé), la page Audit (version contre version) et le panneau des versions.
 */

export type DiffViewMode = "blocks" | "unified" | "visual" | "source" | "deployed";

/** Poids d'un document une fois sérialisé comme l'artefact publié (JSON indenté), en octets. */
export const jsonWeight = (value: unknown): number => {
	try {
		return new Blob([JSON.stringify(value ?? {}, null, 2)]).size;
	} catch {
		return 0;
	}
};

export const formatWeight = (bytes: number): string =>
	bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(2)} Mo` : bytes >= 1024 ? `${(bytes / 1024).toFixed(1)} Ko` : `${bytes} o`;

/** Bandeau de poids : taille du fichier source, du fichier servi, et l'écart signé. */
export const WeightBanner: React.FC<{ before: unknown; after: unknown }> = ({ before, after }) => {
	const served = jsonWeight(before);
	const source = jsonWeight(after);
	const delta = source - served;
	const pct = served > 0 ? (delta / served) * 100 : null;
	return (
		<p className="px-1 text-[11px] text-slate-500" data-testid="diff-weights">
			Fichier source : <span className="font-semibold text-slate-700">{formatWeight(source)}</span>
			{" · "}Fichier servi : <span className="font-semibold text-slate-700">{formatWeight(served)}</span>
			{" · "}écart :{" "}
			<span className={`font-semibold ${delta > 0 ? "text-amber-700" : delta < 0 ? "text-emerald-700" : "text-slate-700"}`}>
				{`${delta > 0 ? "+" : delta < 0 ? "-" : ""}${formatWeight(Math.abs(delta))}`}
				{pct !== null ? ` (${delta > 0 ? "+" : delta < 0 ? "-" : ""}${Math.abs(pct).toFixed(1)} %)` : ""}
			</span>
		</p>
	);
};

/** Un document complet, joliment indenté, dans un cadre défilant. */
const FullDocument: React.FC<{ value: unknown; testId: string }> = ({ value, testId }) => (
	<pre
		className="m-0 max-h-[60vh] max-w-full overflow-auto rounded-xl border border-slate-100 bg-white px-4 py-2 text-[12px] leading-5 text-slate-700"
		data-testid={testId}
	>
		{JSON.stringify(value ?? {}, null, 2)}
	</pre>
);

export interface ChangeDiffViewerProps {
	/** État de référence (déployé, ou version précédente). Objet vide pour une création. */
	before: unknown;
	/** État candidat (brouillon, ou version courante). */
	after: unknown;
	initialMode?: DiffViewMode;
	/** Libellés des deux côtés, affichés en tête (ex. « Déployé » / « Brouillon »). */
	beforeLabel?: string;
	afterLabel?: string;
	/** Troisième mode optionnel, « Rendu côte à côte » : le contenu rendu par l'appelant (la page
	 * Publication y monte les deux pages rendues). Absent = deux modes seulement. */
	visual?: React.ReactNode;
}

const OP_META: Record<string, { label: string; card: string; chip: string }> = {
	added: { label: "Ajouté", card: "border-emerald-200 bg-emerald-50/60", chip: "border-emerald-200 bg-emerald-50 text-emerald-700" },
	removed: { label: "Retiré", card: "border-rose-200 bg-rose-50/60", chip: "border-rose-200 bg-rose-50 text-rose-700" },
	modified: { label: "Modifié", card: "border-amber-200 bg-amber-50/60", chip: "border-amber-200 bg-amber-50 text-amber-700" },
	moved: { label: "Déplacé", card: "border-sky-200 bg-sky-50/60", chip: "border-sky-200 bg-sky-50 text-sky-700" }
};

const clip = (value: string, max = 240): string => (value.length <= max ? value : `${value.slice(0, max)}…`);

/** Une valeur scalaire du diff : vignette/bouton pour un média, texte tronqué sinon. */
const ScalarValue: React.FC<{ value: string }> = ({ value }) => {
	const media = detectMedia(value);
	if (media) return <MediaValue media={media} />;
	return <>{value === "" ? "(vide)" : clip(value)}</>;
};

const BlockCard: React.FC<{ change: ObjectChange }> = ({ change }) => {
	const meta = OP_META[change.op] ?? OP_META.modified;
	return (
		<li className={`rounded-xl border p-3 ${meta.card}`}>
			<div className="flex flex-wrap items-center gap-2">
				<span className={`inline-flex shrink-0 items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${meta.chip}`}>
					{meta.label}
				</span>
				{change.kind === "block" ? (
					<span className="text-sm text-slate-800">
						Bloc <span className="font-semibold">{change.block}</span>
						<span className="text-slate-400"> ({change.field})</span>
					</span>
				) : (
					<span className="text-sm font-semibold text-slate-800">{change.field}</span>
				)}
			</div>
			{change.kind === "scalar" && (change.before !== undefined || change.after !== undefined) && (
				<div className="mt-2 space-y-1 font-mono text-[12px]">
					{change.before !== undefined && (
						<div className="flex items-center gap-1 rounded bg-rose-100/70 px-2 py-1 text-rose-800">
							<span className="select-none">-</span>
							<ScalarValue value={change.before} />
						</div>
					)}
					{change.after !== undefined && (
						<div className="flex items-center gap-1 rounded bg-emerald-100/70 px-2 py-1 text-emerald-800">
							<span className="select-none">+</span>
							<ScalarValue value={change.after} />
						</div>
					)}
				</div>
			)}
			{change.kind === "object" && (
				<p className="mt-1 text-xs text-slate-500">Contenu structuré modifié : le texte unifié montre le détail.</p>
			)}
		</li>
	);
};

export const ChangeDiffViewer: React.FC<ChangeDiffViewerProps> = ({ before, after, initialMode = "blocks", beforeLabel, afterLabel, visual }) => {
	const [mode, setMode] = useState<DiffViewMode>(initialMode);
	const activeMode: DiffViewMode = mode === "visual" && visual === undefined ? "blocks" : mode;

	const blockChanges = useMemo(() => diffObjects(before, after), [before, after]);
	const unifiedRows = useMemo(() => foldUnchanged(objectDiffToSegments(before, after)), [before, after]);
	const hasChanges = blockChanges.length > 0 || unifiedRows.some((r) => r.kind === "line" && r.segment.type !== "unchanged");

	if (!hasChanges) {
		return <div className="px-5 py-12 text-center text-sm text-slate-500">Les deux états sont identiques.</div>;
	}

	return (
		<div className="space-y-3">
			<div className="flex flex-wrap items-center gap-2 px-1">
				<div className="inline-flex rounded-lg border border-slate-200 p-0.5">
					<button
						type="button"
						onClick={() => setMode("blocks")}
						className={`rounded-md px-3 py-1 text-xs font-semibold transition ${activeMode === "blocks" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
					>
						Par blocs
					</button>
					<button
						type="button"
						onClick={() => setMode("unified")}
						className={`rounded-md px-3 py-1 text-xs font-semibold transition ${activeMode === "unified" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
					>
						Texte unifié
					</button>
					{visual !== undefined && (
						<button
							type="button"
							onClick={() => setMode("visual")}
							className={`rounded-md px-3 py-1 text-xs font-semibold transition ${activeMode === "visual" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
						>
							Rendu côte à côte
						</button>
					)}
					<button
						type="button"
						onClick={() => setMode("source")}
						className={`rounded-md px-3 py-1 text-xs font-semibold transition ${activeMode === "source" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
					>
						Fichier source
					</button>
					<button
						type="button"
						onClick={() => setMode("deployed")}
						className={`rounded-md px-3 py-1 text-xs font-semibold transition ${activeMode === "deployed" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
					>
						Fichier servi
					</button>
				</div>
				{(beforeLabel || afterLabel) && (
					<span className="ml-auto text-[11px] text-slate-400">
						{beforeLabel && <span className="text-rose-600">{beforeLabel}</span>}
						{beforeLabel && afterLabel && <span> comparé à </span>}
						{afterLabel && <span className="text-emerald-600">{afterLabel}</span>}
					</span>
				)}
			</div>

			{activeMode === "visual" ? (
				<div data-testid="diff-visual">{visual}</div>
			) : activeMode === "source" ? (
				<div className="space-y-2">
					<WeightBanner before={before} after={after} />
					<FullDocument value={after} testId="diff-source-full" />
				</div>
			) : activeMode === "deployed" ? (
				<div className="space-y-2">
					<WeightBanner before={before} after={after} />
					<FullDocument value={before} testId="diff-deployed-full" />
				</div>
			) : activeMode === "blocks" ? (
				blockChanges.length === 0 ? (
					<p className="px-1 text-xs text-slate-500">
						Aucun champ de premier niveau ne diffère ; le texte unifié montre les écarts fins.
					</p>
				) : (
					<ul className="space-y-2" data-testid="diff-blocks">
						{blockChanges.map((change, index) => (
							<BlockCard key={`${change.field}-${index}`} change={change} />
						))}
					</ul>
				)
			) : (
				<pre className="m-0 max-w-full overflow-x-auto rounded-xl border border-slate-100 bg-white py-2 text-[12px] leading-5" data-testid="diff-unified">
					{unifiedRows.map((row, index) =>
						row.kind === "fold" ? (
							<div key={`fold-${index}`} className="bg-slate-50 px-4 py-1 text-center text-[11px] italic text-slate-400">
								{row.hidden} ligne{row.hidden > 1 ? "s" : ""} inchangée{row.hidden > 1 ? "s" : ""}
							</div>
						) : (
							<div
								key={`line-${index}`}
								className={
									row.segment.type === "added"
										? "bg-emerald-50 px-4 text-emerald-800"
										: row.segment.type === "removed"
											? "bg-rose-50 px-4 text-rose-800"
											: "px-4 text-slate-600"
								}
							>
								<span className="mr-2 inline-block w-3 select-none text-slate-400">
									{row.segment.type === "added" ? "+" : row.segment.type === "removed" ? "-" : " "}
								</span>
								{row.segment.type === "added" ? row.segment.right : row.segment.left}
							</div>
						)
					)}
				</pre>
			)}
		</div>
	);
};

export default ChangeDiffViewer;
